import { useState } from 'react';
import { Box, Stack } from '@mui/material';

import { ApplicationEditableFieldsPanel } from './ApplicationEditableFieldsPanel';
import { ApplicationNotesPanel } from './ApplicationNotesPanel';
import { ApplicationChangeHistoryPanel } from './ApplicationChangeHistoryPanel';

export interface ApplicationDataAndNotesTabProps {
  applicationId: string;
  applicationStatus: string;
}

/**
 * Data, notes and history on one tab.
 *
 * One tab rather than three because a reviewer reads them together: what the
 * values are, what changed, and why we believed it. Splitting them would make
 * every correction a two-tab errand.
 *
 * The editable fields sit on the left where they are acted on; notes and history
 * on the right where they are read. Clicking a field's history icon filters the
 * history panel rather than opening a dialog, so the field list stays in view for
 * comparison.
 */
export function ApplicationDataAndNotesTab({
  applicationId,
  applicationStatus,
}: ApplicationDataAndNotesTabProps) {
  const [historyFieldKey, setHistoryFieldKey] = useState<string | null>(null);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <ApplicationEditableFieldsPanel
        applicationId={applicationId}
        applicationStatus={applicationStatus}
        onShowFieldHistory={setHistoryFieldKey}
      />

      <Stack spacing={2}>
        <ApplicationNotesPanel applicationId={applicationId} />
        <ApplicationChangeHistoryPanel
          applicationId={applicationId}
          fieldKey={historyFieldKey}
          onClearFieldFilter={() => setHistoryFieldKey(null)}
        />
      </Stack>
    </Box>
  );
}
